import { loanWageRelief } from "@/features/financial-compliance/domain/actionFinancials";

import type { ActionDetails, PlayerAction } from "../types";

/**
 * One player action, paired with the current wage of the player it targets.
 *
 * `playerWage` is the effective (override-aware) annual wage of the affected
 * player. It is the "wageRemoved" for a Sell — the spec sources the removed
 * wage from the player, not from an action field (there is no `wageRemoved` on
 * {@link ActionDetails}). A **Loan** removes only the share of that wage the
 * borrowing club covers, read from `details.coveredSharePercent` (SE-7422).
 */
export interface ScenarioActionEntry {
  readonly action: PlayerAction;
  readonly details: ActionDetails | null | undefined;
  readonly playerWage: number;
}

/**
 * The four KPI-bar deltas plus the in/out move counts that drive the
 * Total Players sub-label.
 */
export interface ScenarioDeltas {
  /**
   * Σ(sign.annualSalary + promote.annualSalary)
   * − Σ(sell.wage) − Σ(loan.wage × loan.coveredSharePercent / 100).
   *
   * A loan with no recorded covered share contributes NOTHING here — it is
   * counted in {@link ScenarioDeltas.loansWithoutCoveredShare} instead, so the
   * surface can say the share is missing rather than show a number nobody
   * entered (SE-7422).
   */
  readonly yearlyWageBillDelta: number;
  /** count(signs) − count(sells) − count(loans). Promote does NOT count. */
  readonly totalPlayersDelta: number;
  /** Σ(sell.salePrice) + Σ(loan.loanFee) */
  readonly capitalGainsDelta: number;
  /** Σ(sign.transferFee + sign.agentFee + sign.signingBonus) */
  readonly acquisitionCostDelta: number;
  /** Signs — the players entering the squad for the Total Players count. */
  readonly inCount: number;
  /** Sells + loans — the players leaving the squad for the Total Players count. */
  readonly outCount: number;
  /**
   * Loans whose covered share is absent or out of range (SE-7422). Their wage
   * relief is EXCLUDED from {@link ScenarioDeltas.yearlyWageBillDelta} rather
   * than guessed at 100% or silently taken as 0, so a surface can disclose
   * that the figure it shows is incomplete.
   */
  readonly loansWithoutCoveredShare: number;
}

const ZERO_DELTAS: ScenarioDeltas = {
  yearlyWageBillDelta: 0,
  totalPlayersDelta: 0,
  capitalGainsDelta: 0,
  acquisitionCostDelta: 0,
  inCount: 0,
  outCount: 0,
  loansWithoutCoveredShare: 0,
};

/**
 * Computes the KPI-bar deltas for a set of scenario actions per Section 3 of
 * the Squad Planning PRD.
 *
 * The formulas, and the deliberate exclusions:
 * - **Promote** adds its annual salary to the wage bill but does NOT change the
 *   Total Players count (the player was already in the organisation) and does
 *   NOT contribute to Capital Gains or Acquisition Cost.
 * - **wageRemoved** for a Sell is the affected player's own wage
 *   ({@link ScenarioActionEntry.playerWage}), not a field on the action.
 * - **Loan** removes only `playerWage × coveredSharePercent / 100` — the share
 *   the BORROWING club covers (SE-7324 made that a real persisted input; the
 *   generated schema described it the other way round until backend SE-7403).
 *   This used to remove the whole wage, which was an unstated assumption of a
 *   100% covered share and is the defect SE-7422 fixes. A loan with no usable
 *   share removes NOTHING and is counted in
 *   {@link ScenarioDeltas.loansWithoutCoveredShare} — never assumed at 100,
 *   never silently taken as 0.
 * @param entries - The scenario's actions paired with affected-player wages.
 * @returns The four deltas, the in/out move counts, and the unrecorded-share count.
 */
export const computeScenarioDeltas = (
  entries: readonly ScenarioActionEntry[]
): ScenarioDeltas => {
  return entries.reduce<ScenarioDeltas>(
    (acc, { action, details, playerWage }) => {
      switch (action) {
        case "Sign":
          return {
            ...acc,
            yearlyWageBillDelta:
              acc.yearlyWageBillDelta + (details?.annualSalary ?? 0),
            totalPlayersDelta: acc.totalPlayersDelta + 1,
            acquisitionCostDelta:
              acc.acquisitionCostDelta +
              (details?.transferFee ?? 0) +
              (details?.agentFee ?? 0) +
              (details?.signingBonus ?? 0),
            inCount: acc.inCount + 1,
          };
        case "Promote":
          return {
            ...acc,
            yearlyWageBillDelta:
              acc.yearlyWageBillDelta + (details?.annualSalary ?? 0),
          };
        case "Sell":
          return {
            ...acc,
            yearlyWageBillDelta: acc.yearlyWageBillDelta - playerWage,
            totalPlayersDelta: acc.totalPlayersDelta - 1,
            capitalGainsDelta:
              acc.capitalGainsDelta + (details?.salePrice ?? 0),
            outCount: acc.outCount + 1,
          };
        case "Loan": {
          // Relief, not the whole wage: `coveredSharePercent` is the share the
          // BORROWING club covers, so that is exactly what leaves this club's
          // bill (SE-7422). `null` = no recorded share — excluded and counted.
          const relief = loanWageRelief(
            playerWage,
            details?.coveredSharePercent
          );
          return {
            ...acc,
            yearlyWageBillDelta: acc.yearlyWageBillDelta - (relief ?? 0),
            totalPlayersDelta: acc.totalPlayersDelta - 1,
            capitalGainsDelta: acc.capitalGainsDelta + (details?.loanFee ?? 0),
            outCount: acc.outCount + 1,
            loansWithoutCoveredShare:
              acc.loansWithoutCoveredShare + (relief == null ? 1 : 0),
          };
        }
        default:
          return acc;
      }
    },
    ZERO_DELTAS
  );
};
